import { Box, Divider } from "@mui/material";
import { useMemo, useState } from "react";

import Flex from "../../../../../components/Flex";
import OutlineCard from "../../../../../components/OutlineCard";
import Text from "../../../../../components/Text";
import NumberInput from "../../../../../components/NumberInput";
import { CustomSelect } from "../../../../../components/CustomSelect";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import {
  DEFAULT_BORDER_COLOR,
  DEFAULT_LABEL_COLOR,
  DEFAULT_LABEL_COLOR_2,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../utils/utils";
import { useGetAllAppointmentConfigurations } from "../../../../../queries/AppointmentConfiguration/use-get-appointment-configuration";
import { useAddAppointmentConfiguration } from "../../../../../queries/AppointmentConfiguration/use-add-appointment-configuration";
import { useUpdateAppointmentConfiguration } from "../../../../../queries/AppointmentConfiguration/use-update-appointment-configuration";

type TimeUnit = "HOURS" | "DAYS" | "MINUTES";

type AppointmentConfigValues = {
  cancelBefore: string;
  cancelUnit: TimeUnit;
  rescheduleBefore: string;
  rescheduleUnit: TimeUnit;
};

const TIME_UNIT_OPTIONS = [
  { label: "Minutes", value: "MINUTES" },
  { label: "Hours", value: "HOURS" },
  { label: "Days", value: "DAYS" },
];

const INITIAL_VALUES: AppointmentConfigValues = {
  cancelBefore: "24",
  cancelUnit: "HOURS",
  rescheduleBefore: "48",
  rescheduleUnit: "HOURS",
};

const AppointmentConfigurationForm = () => {
  const showSnackbar = useSnackbar();

  const { data: appointmentConfig, refetch } =
    useGetAllAppointmentConfigurations({ xTENANTID: getXtenantId() ?? "" });
  const { mutateAsync: addAppointmentConfig } =
    useAddAppointmentConfiguration();
  const { mutateAsync: updateAppointmentConfig } =
    useUpdateAppointmentConfiguration();

  const record = useMemo(() => {
    const list =
      (appointmentConfig as any)?.content ??
      (appointmentConfig as any)?.data?.content ??
      (Array.isArray(appointmentConfig) ? appointmentConfig : []);
    return list?.[0];
  }, [appointmentConfig]);

  const prefilledValues: AppointmentConfigValues = useMemo(
    () => ({
      cancelBefore:
        record?.cancelAppointmentTime != null
          ? String(record.cancelAppointmentTime)
          : INITIAL_VALUES.cancelBefore,
      cancelUnit:
        (record?.cancelAppointmentUnit as TimeUnit) ??
        INITIAL_VALUES.cancelUnit,
      rescheduleBefore:
        record?.rescheduleAppointmentTime != null
          ? String(record.rescheduleAppointmentTime)
          : INITIAL_VALUES.rescheduleBefore,
      rescheduleUnit:
        (record?.rescheduleAppointmentUnit as TimeUnit) ??
        INITIAL_VALUES.rescheduleUnit,
    }),
    [record]
  );

  const existingUuid: string | undefined = record?.uuid;

  const [draft, setDraft] = useState<AppointmentConfigValues | null>(null);
  const values = draft ?? prefilledValues;

  const isDisable =
    draft !== null &&
    (draft.cancelBefore !== prefilledValues.cancelBefore ||
      draft.cancelUnit !== prefilledValues.cancelUnit ||
      draft.rescheduleBefore !== prefilledValues.rescheduleBefore ||
      draft.rescheduleUnit !== prefilledValues.rescheduleUnit);

  const handleChange =
    <K extends keyof AppointmentConfigValues>(field: K) =>
    (val: AppointmentConfigValues[K]) =>
      setDraft((prev) => ({ ...(prev ?? prefilledValues), [field]: val }));

  const handleSave = () => {
    const payload = {
      uuid: existingUuid,
      cancelAppointmentTime: Number(values.cancelBefore) || 0,
      cancelAppointmentUnit: values.cancelUnit,
      rescheduleAppointmentTime: Number(values.rescheduleBefore) || 0,
      rescheduleAppointmentUnit: values.rescheduleUnit,
    };

    const apiCall = existingUuid
      ? updateAppointmentConfig({
          appointmentConfigurationDto: payload,
          xTENANTID: getXtenantId() ?? "",
        })
      : addAppointmentConfig({
          appointmentConfigurationDto: payload,
          xTENANTID: getXtenantId() ?? "",
        });

    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        setDraft(null);
        refetch();
      },
    });
  };

  return (
    <OutlineCard sx={{ height: "100%" }}>
      <Flex flexDirection={"column"} gap={"4px"} marginBottom={"16px"}>
        <Text fontSize={"16px"} fontWeight={600} color={DEFAULT_LABEL_COLOR}>
          Appointment Configuration
        </Text>
        <Text fontSize={"13px"} color={DEFAULT_LABEL_COLOR_2}>
          Configure the time window within which patients are allowed to cancel
          or reschedule appointments.
        </Text>
      </Flex>

      <Box
        sx={{
          borderRadius: "6px",
          padding: "20px",
        }}
      >
        <ConfigRow
          title="Cancel Appointment by Patient"
          value={values.cancelBefore}
          unit={values.cancelUnit}
          onValueChange={handleChange("cancelBefore")}
          onUnitChange={(v) => handleChange("cancelUnit")(v as TimeUnit)}
        />

        <Divider sx={{ marginY: "16px", borderColor: DEFAULT_BORDER_COLOR }} />

        <ConfigRow
          title="Reschedule Appointment by Patient"
          value={values.rescheduleBefore}
          unit={values.rescheduleUnit}
          onValueChange={handleChange("rescheduleBefore")}
          onUnitChange={(v) => handleChange("rescheduleUnit")(v as TimeUnit)}
        />
      </Box>

      <Flex justifyContent={"flex-end"} marginTop={"16px"}>
        <StyledButtonV2
          label="Save"
          onClick={handleSave}
          disabled={!isDisable}
        />
      </Flex>
    </OutlineCard>
  );
};

type ConfigRowProps = {
  title: string;
  value: string;
  unit: TimeUnit;
  onValueChange: (val: string) => void;
  onUnitChange: (val: string) => void;
};

const ConfigRow = ({
  title,
  value,
  unit,
  onValueChange,
  onUnitChange,
}: ConfigRowProps) => (
  <Flex flexDirection={"column"} gap={"12px"}>
    <Text fontSize={"14px"} fontWeight={600} color={DEFAULT_LABEL_COLOR}>
      {title}
    </Text>
    <Flex alignItems={"center"} gap={"12px"} flexWrap={"wrap"}>
      <Text fontSize={"13px"} color={DEFAULT_LABEL_COLOR}>
        Before Appointment :
      </Text>
      <Box width={"96px"}>
        <NumberInput
          blockedKeys={["-", "+", ".", ",", "e", "E"]}
          min={0}
          max={999}
          textFieldProps={{
            value,
            onChange: (e) => onValueChange(e.target.value),
            size: "small",
          }}
        />
      </Box>
      <Box width={"120px"}>
        <CustomSelect
          options={TIME_UNIT_OPTIONS}
          value={unit}
          onChange={(v) => onUnitChange(v ?? "HOURS")}
        />
      </Box>
    </Flex>
  </Flex>
);

export default AppointmentConfigurationForm;
